import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  HeartIcon, 
  StarIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import './MobileProductCard.css';
import { Product } from '../features/products/productModels';
import { CategoryProduct } from '../features/categories/categoryModels';
import { useCart } from '../contexts/CartContext';

interface MobileProductCardProps {
  product: Product|CategoryProduct;
  
  onAddToCart: (product: Product|CategoryProduct) => void;
  onToggleWishlist: (productId: number) => void;
  isInWishlist: boolean;
}

const MobileProductCard: React.FC<MobileProductCardProps> = ({
  product,
  onAddToCart,
  onToggleWishlist,
  isInWishlist
}) => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const [isHovered, setIsHovered] = useState(false);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
  if (product) {
    addToCart(product);
  }
  };


  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // onToggleWishlist(product._id);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <StarIcon
        key={index}
        className={`star ${index < Math.floor(rating) ? 'filled' : ''}`}
      />
    ));
  };

  return (
    <div 
      className={`mobile-product-card ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/product/${product._id}`} className="mobile-product-link">
        {/* Product Image */}
        <div className="mobile-product-image-container">
          <img 
            src={product.images?.[0] || product.imagePath} 
            alt={product.productName}
            className="mobile-product-image"
          />
          
          {/* Badges */}
          <div className="mobile-product-badges">
            {product.enable && <span className="badge new">{t('productCard.new')}</span>}
            {product.sale && <span className="badge bestseller">{t('productCard.best')}</span>}
            {product.saleDescription && (
              <span className="badge discount">%</span>
            )}
          </div>

          {/* Quick Actions */}
          <div className="mobile-quick-actions">
            <button
              className={`mobile-quick-action wishlist ${isInWishlist ? 'active' : ''}`}
              onClick={handleToggleWishlist}
              title={isInWishlist ? t('productCard.removeFromWishlist') : t('productCard.addToWishlist')}
            >
              {isInWishlist ? (
                <HeartSolidIcon className="action-icon" />
              ) : (
                <HeartIcon className="action-icon" />
              )}
            </button>
          </div>

          {/* Stock Status */}
          {!product.enable && (
            <div className="mobile-out-of-stock-overlay">
              <span>{t('productCard.outOfStock')}</span>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="mobile-product-info">
          {/* Brand */}
          <div className="mobile-product-brand">{product.brand}</div>
          
          {/* Product Name */}
          <h3 className="mobile-product-name">{product.productName}</h3>
          


          {/* Price */}
          <div className="mobile-product-price">
          <span className="current-price">₪{ product?.salePrice && product?.salePrice>0 && product.sale ? product.salePrice : product.price}</span>
            {product.sale && (
              <span className="original-price">₪{product.price}</span>
            )}
          </div>

          {/* Add to Cart Button */}
          <button
            className={`mobile-add-to-cart-button ${!product.enable ? 'disabled' : ''}`}
            onClick={handleAddToCart}
            disabled={!product.enable}
          >
            <ShoppingCartIcon className="cart-icon" />
            {product.enable ? t('productCard.addToCart') : t('productCard.outOfStock')}
          </button>
        </div>
      </Link>
    </div>
  );
};

export default MobileProductCard;
